/* eslint-disable max-len */
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import JsonQuery from 'json-query';
import { useEffect, useState } from 'react';
import { saveAs } from 'file-saver';
import type { CellObject, ColInfo, Range, RowInfo, WorkBook, WorkSheet } from 'xlsx-js-style';
import { hooks as commonHooks, types as commonTypes } from '@sitebuilder/common';
import Table, { TableColumn, TableOptions } from '../Table/Table';
import IntegratedDatePicker from '../GrowthChart/IntegratedDatePicker';
import { IntegratedDatePickerOptions } from '../GrowthChart/GrowthChart';
import {
    getTextSchemaProperties,
    getStyleSchemaProperties,
    getLayoutSchemaProperties,
} from '../utils';
import DaySelect from './DaySelect';
import MonthSelect from './MonthSelect';
import pricesTableStyles, { pricesTableThemeStyles } from './PricesTable.styles';
import YearSelect from './YearSelect';

export interface SheetTabOption {
    name: string;
    showFundName: boolean;
}

interface Date {
    day: number;
    month: number;
    year: number;
}

interface WidgetProperties extends commonTypes.WidgetPropertiesBase, TableOptions {
    startDateLabel?: string;
    endDateLabel?: string;
    dateDatakey: string;
    filterButtonName: string;
    tableOptions: TableOptions;
    downloadButtonName: string;
    transformData?: boolean;
    functionToTransformData?: string;
    downloadFileName: string;
    displayDownloadButtonTop?: boolean;
    hideColumnsInCsv?: string;
    fileType?: string;
    excelTabs: SheetTabOption[];
    csvOptions: SheetTabOption;
    typographyClass?: string;
    isUseIntegratedDatePicker?: boolean;
    integratedDatePickerOptions: IntegratedDatePickerOptions;
}

const PricesTable = (props: commonTypes.WidgetInstanceProps) => {
    const widgetProps =
        commonHooks.useWidgetProperties<WidgetProperties>(props.widgetInstanceId) || {};
    const { styles, rows, transformData, functionToTransformData, ...rest } = widgetProps;
    const { data } = props;
    const theme = data?.themeSelected || props?.theme;
    const i18n = commonHooks.useLocalization();

    let isRender = {
        transformedData: data,
    };

    const fileName = i18n.format(rest.downloadFileName, data);

    try {
        // eslint-disable-next-line no-new-func
        const transformFunction = Function(`${functionToTransformData}`)();
        const _transformedData = transformData && transformFunction(data);

        if (_transformedData) {
            isRender = { ...isRender, ..._transformedData };
        }
    } catch (e) {
        console.log(e);
    }

    const _data = isRender.transformedData;

    const [_pricesData, setPricesData] = useState<any>([]);

    const pricesData: any = rows ? JsonQuery(rows as string, { data: _data }).value : _data;

    // get min date of price data
    const minDate = new Date(pricesData?.[0]?.[rest?.dateDatakey]);

    // get max date of price data
    const maxDate = new Date(pricesData?.[pricesData?.length - 1]?.[rest?.dateDatakey]);

    const [startDate, setStartDate] = useState<Date>({
        day: maxDate.getDate() + 1,
        month: maxDate.getMonth(),
        year: maxDate.getFullYear() - 1,
    });
    const [endDate, setEndDate] = useState<Date>({
        day: maxDate.getDate() + 1,
        month: maxDate.getMonth(),
        year: maxDate.getFullYear(),
    });
    const _startDate = new Date(startDate.year, startDate.month, startDate.day);
    const _endDate = new Date(endDate.year, endDate.month, endDate.day);

    useEffect(() => {
        const filteredData = Array.isArray(pricesData)
            ? pricesData
                  .filter(
                      (p: any) =>
                          _startDate <= new Date(p?.[rest?.dateDatakey]) &&
                          _endDate >= new Date(p?.[rest?.dateDatakey])
                  )
                  .reverse()
            : [];
        setPricesData(filteredData);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const onStartDateChange = (e: React.ChangeEvent<HTMLSelectElement>, key: string) => {
        const value: number = parseFloat(e.target.value);
        setStartDate({ ...startDate, [key]: value });
    };

    const onEndDateChange = (e: React.ChangeEvent<HTMLSelectElement>, key: string) => {
        const value: number = parseFloat(e.target.value);
        setEndDate({ ...endDate, [key]: value });
    };

    const onFilterClick = () => {
        if (_startDate < _endDate) {
            const _pricesData = pricesData.filter(
                (p: any) =>
                    _startDate <= new Date(p?.[rest?.dateDatakey]) &&
                    _endDate >= new Date(p?.[rest?.dateDatakey])
            );
            setPricesData(_pricesData?.slice().reverse());
        } else {
            setPricesData([]);
        }
    };

    const onDownloadClick = () => {
        const fileType: string =
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
        const firstColumnTitle: string = `${JsonQuery('overview.fundName', { data }).value} ${
            JsonQuery('overview.seriesName', { data }).value
        }`;
        const columns: TableColumn[] =
            rest.columns?.filter(
                (_, i: number) => !rest.hideColumnsInCsv?.split(',').includes(`${i}`)
            ) || [];
        let csvColumns: TableColumn[] = [...columns];
        // Initialize workbook
        import('xlsx-js-style').then(({ utils, write }) => {
            const wb: WorkBook = utils?.book_new();
            const tabs: SheetTabOption[] =
                rest.fileType === '.xlsx' ? rest?.excelTabs : [rest?.csvOptions];

            // process each tabs options
            tabs?.forEach((opt: SheetTabOption) => {
                const rowsData: any = []; // worksheet data
                const wsCols: ColInfo[] = []; // worksheet column options
                const wsMerges: Range[] = []; // worksheet merges options
                const wsRows: RowInfo[] = []; // worksheet row options

                // add first coulmn to worksheet
                if (opt?.showFundName) {
                    const firstColumn: TableColumn = {
                        label: firstColumnTitle,
                        value: '',
                    };
                    csvColumns = [firstColumn, ...csvColumns];
                }

                _pricesData?.length &&
                    _pricesData?.forEach((r: any) => {
                        const row: any = {};
                        csvColumns?.forEach((c: TableColumn, index: number) => {
                            const label = i18n.format(c.label || '', data) || '';
                            const value = i18n.format(c.value || '', { ...r, ...data });
                            row[label] = value;
                            wsCols[index] = {
                                wch: label.length >= value.length ? label.length : value.length,
                            };
                        });
                        // add rows to worksheet
                        rowsData.push(row);
                    });

                const ws: WorkSheet = utils?.json_to_sheet(rowsData);
                if (ws) {
                    ws['!cols'] = wsCols;
                    ws['!merges'] = wsMerges;
                    ws['!rows'] = wsRows;

                    // apply style to worksheet
                    for (const i in ws as CellObject) {
                        if (typeof ws[i] === 'object' && ws[i].v === '') {
                            ws[i].s = {
                                alignment: {
                                    wrapText: true,
                                },
                            };
                        }
                    }
                }

                // append each sheet to workbook
                utils?.book_append_sheet(wb, ws, `${i18n.format(opt?.name, data)}`);
            });
            let excelBuffer: string = '';
            if (wb) {
                excelBuffer = write(wb, { bookType: 'xlsx', type: 'array' }) || '';
            }
            // check file type for worksheet
            if (rest?.fileType === '.xlsx') {
                const _data = new Blob([excelBuffer], { type: fileType });
                saveAs(_data, `${fileName}.xlsx` || 'download.xlsx');
            } else if (rest?.fileType === '.csv') {
                import('xlsx').then(({ read }) => {
                    // read xlsx file
                    const rs = read(excelBuffer, { type: 'file' });
                    // generate csv file
                    const csvBuffer: string = write(rs, { bookType: 'csv', type: 'array' });
                    const blobCSV = new Blob([csvBuffer], { type: 'text/plain;charset=UTF-8' });
                    saveAs(blobCSV, `${fileName}.csv` || 'download.csv');
                });
            }
        });
    };

    const downloadButtonHtml = (onClickFunction: any) => {
        return (
            <button
                className="download-all-button"
                data-testid="download-all-button"
                onClick={() => onClickFunction()}
            >
                <span
                    dangerouslySetInnerHTML={{
                        __html: i18n.format(rest.downloadButtonName || '', data),
                    }}
                />
            </button>
        );
    };

    return (
        <div
            className={`prices-table-container ${
                widgetProps?.typographyClass ? widgetProps.typographyClass : ''
            }`}
            css={css`
                ${!theme ? pricesTableStyles : pricesTableThemeStyles}${getTextSchemaProperties(
                    widgetProps,
                    data?.colorSpace
                )}${getStyleSchemaProperties(widgetProps)}${getLayoutSchemaProperties(
                    widgetProps
                )}${styles}
            `}
        >
            {rest.displayDownloadButtonTop ? downloadButtonHtml(onDownloadClick) : ''}
            <Table pricesData={_pricesData} {...widgetProps} {...props} />
            <div className="date-input">
                {!rest.isUseIntegratedDatePicker ? (
                    <>
                        <div className="start-date-container">
                            <label
                                className="start-date-label"
                                dangerouslySetInnerHTML={{
                                    __html: i18n.format(rest.startDateLabel || '', data),
                                }}
                            />
                            <br />
                            <MonthSelect selectedDate={_startDate} onChange={onStartDateChange} />
                            <DaySelect selectedDate={_startDate} onChange={onStartDateChange} />
                            <YearSelect
                                minDate={minDate}
                                maxDate={maxDate}
                                selectedDate={_startDate}
                                onChange={onStartDateChange}
                            />
                        </div>
                        <div className="end-date-container">
                            <label
                                className="start-date-label"
                                dangerouslySetInnerHTML={{
                                    __html: i18n.format(rest.endDateLabel || '', data),
                                }}
                            />
                            <br />
                            <MonthSelect selectedDate={_endDate} onChange={onEndDateChange} />
                            <DaySelect selectedDate={_endDate} onChange={onEndDateChange} />
                            <YearSelect
                                minDate={minDate}
                                maxDate={maxDate}
                                selectedDate={_endDate}
                                onChange={onEndDateChange}
                            />
                        </div>
                    </>
                ) : pricesData?.[0]?.[rest?.dateDatakey] &&
                  pricesData?.[pricesData?.length - 1]?.[rest?.dateDatakey] ? (
                    <IntegratedDatePicker
                        data={data}
                        minDate={pricesData?.[0]?.[rest?.dateDatakey]}
                        maxDate={pricesData?.[pricesData?.length - 1]?.[rest?.dateDatakey]}
                        startDateLabel={rest?.startDateLabel}
                        endDateLabel={rest?.endDateLabel}
                        dateFormat={rest?.integratedDatePickerOptions?.dateFormat}
                        datePickerIconPosition={
                            rest?.integratedDatePickerOptions?.datePickerIconPosition
                        }
                        datePickerIconType={rest?.integratedDatePickerOptions?.datePickerIconType}
                        datePickerIcon={rest?.integratedDatePickerOptions?.datePickerIcon}
                        datePickerImage={rest?.integratedDatePickerOptions?.datePickerImage}
                        onChangeDateRange={(sDate, eDate) => {
                            setEndDate(eDate);
                            setStartDate(sDate);
                        }}
                    />
                ) : (
                    <></>
                )}
                <button className="filter-all-button" onClick={() => onFilterClick()}>
                    <span
                        dangerouslySetInnerHTML={{
                            __html: i18n.format(rest.filterButtonName || '', data),
                        }}
                    />
                </button>
                {!rest.displayDownloadButtonTop ? downloadButtonHtml(onDownloadClick) : ''}
            </div>
        </div>
    );
};

export default PricesTable;
